// src/context/AuthContext.jsx - Complete implementation with setUser
import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useApi';

// Create the context
const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  
  // Birden fazla yönlendirmeyi engellemek için ref
  const isRedirectingRef = useRef(false);
  
  // Try to get initial state from local storage first for fast UI rendering
  const initialIsAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
  let initialUser = null;
  try {
    const userJson = localStorage.getItem('currentUser');
    if (userJson) {
      initialUser = JSON.parse(userJson);
    }
  } catch (e) {
    console.error('Error parsing user data:', e);
  }
  
  const [isAuthenticated, setIsAuthenticated] = useState(initialIsAuthenticated);
  const [user, setCurrentUser] = useState(initialUser);
  const [isAuthenticating, setIsAuthenticating] = useState(!initialIsAuthenticated);
  
  // OTP state
  const [waitingForOTP, setWaitingForOTP] = useState(false);
  const [pendingEmail, setPendingEmail] = useState(null);
  const [isNewUser, setIsNewUser] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  
  // React Query hook for checking auth status (only for authenticated users)
  const { data: userData, isLoading: isUserLoading, error: userError } = useAuth();

  // setUser function to properly update the user data
  const setUser = (userData) => {
    if (userData) {
      // Update state
      setCurrentUser(userData);
      
      // Update localStorage
      localStorage.setItem('currentUser', JSON.stringify(userData));
      
      // Update authentication state if user is now active (role > 0)
      if (userData.role > 0) {
        setIsAuthenticated(true);
        localStorage.setItem('isAuthenticated', 'true');
      }
    }
  };
  
  // Auth error handler fonksiyonu
  const handleAuthError = useRef((event) => {
    console.log('Auth error event alındı:', event?.detail);
    
    // Zaten yönlendirme yapılıyorsa, tekrar yapma
    if (isRedirectingRef.current) {
      console.log('Zaten yönlendirme yapılıyor, atlanıyor...');
      return;
    }
    
    // Yönlendirme flag'ini aktif et
    isRedirectingRef.current = true;
    
    // Tüm auth state'leri temizle
    setIsAuthenticated(false);
    setCurrentUser(null);
    setWaitingForOTP(false);
    setPendingEmail(null);
    setIsNewUser(false);
    setOtpVerified(false);
    
    // LocalStorage'ı temizle
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('currentUser');
    
    // Login sayfasına yönlendir
    setTimeout(() => {
      navigate('/login', { replace: true });
      // Yönlendirmeden sonra flag'i sıfırla
      setTimeout(() => {
        isRedirectingRef.current = false;
      }, 1000);
    }, 100);
  });
  
  // Auth error event listener'ı kur
  useEffect(() => {
    const currentHandler = handleAuthError.current;
    
    // Global auth-error event'ini dinle
    window.addEventListener('auth-error', currentHandler);
    
    return () => {
      window.removeEventListener('auth-error', currentHandler);
    };
  }, []);
  
  // React Query'den gelen auth durumunu izle
  useEffect(() => {
    // Auth check hatası varsa
    if (userError) {
      console.error('Auth check hatası:', userError);
      
      // 401 veya 403 hatası varsa auth error handler'ı çağır
      if (userError?.response?.status === 401 || 
          userError?.response?.status === 403 ||
          userError?.authError) {
        handleAuthError.current({ detail: { source: 'useAuth', error: userError } });
      }
    }
    
    // Auth verisi geldi ve authenticated ise
    if (isAuthenticated && userData && !userError) {
      const authStatus = userData.data?.authenticated || false;
      
      if (authStatus) {
        setCurrentUser(userData.data.user);
      } else if (userData.data && userData.data.authenticated === false) {
        // Server authenticated olmadığını söylüyor
        console.log('Server kullanıcının authenticated olmadığını bildiriyor');
        handleAuthError.current({ detail: { source: 'authCheck', authenticated: false } });
      }
    }
    
    if (!isUserLoading) {
      setIsAuthenticating(false);
    }
  }, [userData, isUserLoading, userError, isAuthenticated]);
  
  // Periyodik auth kontrolü (her 5 dakikada bir)
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const checkAuthStatus = async () => {
      try {
        const response = await fetch('https://bank.wells.finance/api/v1/auth/user', {
          method: 'GET',
          credentials: 'include',
        });
        
        if (!response.ok || response.status === 401) {
          console.error('Periyodik auth kontrolü başarısız');
          handleAuthError.current({ 
            detail: { 
              source: 'periodicCheck', 
              status: response.status 
            } 
          });
        } else {
          const data = await response.json();
          if (!data.data?.authenticated) {
            console.error('Periyodik kontrol: Kullanıcı authenticated değil');
            handleAuthError.current({ 
              detail: { 
                source: 'periodicCheck', 
                authenticated: false 
              } 
            });
          }
        }
      } catch (error) {
        console.error('Periyodik auth kontrol hatası:', error);
      }
    };
    
    // İlk kontrolü 30 saniye sonra yap
    const firstCheckTimeout = setTimeout(checkAuthStatus, 30000);
    
    // Sonra her 5 dakikada bir kontrol et
    const interval = setInterval(checkAuthStatus, 5 * 60 * 1000);
    
    return () => {
      clearTimeout(firstCheckTimeout);
      clearInterval(interval);
    };
  }, [isAuthenticated]);
  
  // Update localStorage when auth state changes
  useEffect(() => {
    if (isAuthenticated) {
      localStorage.setItem('isAuthenticated', 'true');
      if (user) {
        localStorage.setItem('currentUser', JSON.stringify(user));
      }
    } else {
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('currentUser');
    }
  }, [isAuthenticated, user]);
  
  // Login function - Send OTP
  const login = async (email) => {
    try {
      const result = await fetch('https://bank.wells.finance/api/v1/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ email }),
      });
      
      const data = await result.json();
      
      if (data.success) {
        // Store CSRF token if provided
        if (data.data?.csrf_token) {
          localStorage.setItem('csrf_token', data.data.csrf_token);
        }
        
        setPendingEmail(email);
        setWaitingForOTP(true);
        setIsNewUser(data.data?.is_new_user || false);
        setOtpVerified(false);
        
        return true;
      }
      
      throw new Error(data.message || 'Failed to send verification code');
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };
  
  // Verify OTP
  const verifyOTP = async (otp) => {
    if (!pendingEmail) throw new Error('No email provided');
    
    try {
      const requestData = { 
        email: pendingEmail, 
        otp
      };
      
      // Add CSRF token if available
      const csrfToken = localStorage.getItem('csrf_token');
      if (csrfToken) {
        requestData.csrf_token = csrfToken;
      }
      
      const result = await fetch('https://bank.wells.finance/api/v1/auth/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(requestData),
      });
      
      const data = await result.json();
      
      if (data.success) {
        // Store CSRF token if provided
        if (data.data?.csrf_token) {
          localStorage.setItem('csrf_token', data.data.csrf_token);
        }
        
        // Set OTP as verified
        setOtpVerified(true);
        setWaitingForOTP(false);
        
        // Check if this is a new user based on the response
        const userIsNew = data.data?.is_new_user || false;
        
        if (!userIsNew) {
          // Existing user - complete login
          setIsAuthenticated(true);
          setCurrentUser(data.data?.user);
          setPendingEmail(null);
          setIsNewUser(false);
          setOtpVerified(false);
          
          return true; // Existing user login successful
        } else {
          // New user - needs profile setup
          setIsNewUser(true);
          
          return { needsProfileSetup: true };
        }
      }
      
      throw new Error(data.message || 'Invalid verification code');
    } catch (error) {
      console.error('OTP verification error:', error);
      throw error;
    }
  };
  
  // Complete registration for new users
  const completeRegistration = async (profileData) => {
    if (!pendingEmail) {
      throw new Error('No email provided. Please start the registration process again.');
    }
    
    if (!otpVerified) {
      throw new Error('OTP not verified. Please verify your email first.');
    }
    
    try {
      const requestData = {
        ...profileData,
      };
      
      // Add CSRF token if available
      const csrfToken = localStorage.getItem('csrf_token');
      if (csrfToken) {
        requestData.csrf_token = csrfToken;
      }
      
      const result = await fetch('https://bank.wells.finance/api/v1/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(requestData),
      });
      
      const data = await result.json();
      
      if (data.success) {
        // Store CSRF token if provided
        if (data.data?.csrf_token) {
          localStorage.setItem('csrf_token', data.data.csrf_token);
        }
        
        // Set auth state
        setIsAuthenticated(true);
        setCurrentUser(data.data?.user);
        setWaitingForOTP(false);
        setPendingEmail(null);
        setIsNewUser(false);
        setOtpVerified(false);
        
        return true;
      }
      
      throw new Error(data.message || 'Failed to create account');
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  };
  
  // Logout function
  const logout = async () => {
    try {
      const result = await fetch('https://bank.wells.finance/api/v1/auth/logout', {
        method: 'GET',
        credentials: 'include',
      });
      
      console.log('Logout response received');
      
      // Clear all state
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('currentUser');
      
      setIsAuthenticated(false);
      setCurrentUser(null);
      setWaitingForOTP(false);
      setPendingEmail(null);
      setIsNewUser(false);
      setOtpVerified(false);
      
      navigate('/login', { replace: true });
      return true;
    } catch (error) {
      console.error('Logout error:', error);
      
      // Still clear auth state even if API call fails
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('currentUser');
      setIsAuthenticated(false);
      setCurrentUser(null);
      setWaitingForOTP(false);
      setPendingEmail(null);
      setIsNewUser(false);
      setOtpVerified(false);
      
      navigate('/login', { replace: true });
      return false;
    }
  };
  
  // Build the context value
  const contextValue = {
    isAuthenticating,
    isAuthenticated,
    user,
    login,
    verifyOTP,
    completeRegistration,
    logout,
    setUser, // Added setUser function
    waitingForOTP,
    pendingEmail,
    isNewUser,
    otpVerified,
  };
  
  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook to use the auth context
export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;